/**
 * Build the graph-editor URL for navigating to a backend-linked
 * drawing_to_drawing connector target (connected_to_* node properties).
 *
 * Keeps the surrounding navigation context alive across the jump:
 * - grouped multi-page sets carry multiPageSet + docIds (docIds is only
 *   meaningful alongside multiPageSet — every consumer requires both)
 * - single multi-page PDFs carry pnidPages, but only when the target graph
 *   is one of this PDF's pages — cross-document links must not inherit
 *   another document's page map
 */
export interface ConnectorNavTarget {
  graphId: string
  nodeId?: string
  documentId?: string
}

export interface ConnectorNavContext {
  multiPageSetId: string | null
  docIds: string | null
  pnidPages: string | null
  /** Graph ids of this PDF's pages, parsed from the pnidPages param. */
  pnidPageGraphIds: string[] | null
}

export function buildConnectorNavUrl(
  target: ConnectorNavTarget,
  ctx: ConnectorNavContext
): string {
  const params = new URLSearchParams({ editor: 'd3' })
  if (ctx.multiPageSetId) {
    params.set('multiPageSet', ctx.multiPageSetId)
    if (ctx.docIds) params.set('docIds', ctx.docIds)
  }
  if (target.documentId) params.set('documentId', target.documentId)
  if (ctx.pnidPages && ctx.pnidPageGraphIds?.includes(target.graphId)) {
    params.set('pnidPages', ctx.pnidPages)
  }
  if (target.nodeId) params.set('highlightNode', target.nodeId)
  return `/graph-editor/${encodeURIComponent(target.graphId)}?${params.toString()}`
}
